import { useRef, useState } from 'react';
import { useAppSelector } from '../../stores/redux.store';
import { useAnalyticsClient } from '../../utilities/analytics/analytics-web-client';

type EmailLinkResponse = {
  redirectTo?: string;
  redirectUri?: string;
  token?: string;
  error?: string;
};

export const useEmailLinkRequest = (pageId: string) => {
  const csrfToken = useAppSelector(state => state.csrfToken);
  const analytics = useAnalyticsClient();
  const pending = useRef(false);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (
    endpoint: string,
    body: { token: string; otpCode?: string },
    responseKey: 'redirectTo' | 'redirectUri' | 'token'
  ): Promise<string | undefined> => {
    if (pending.current) return;
    pending.current = true;
    setLoading(true);
    setError(undefined);
    let result = 'unexpected_error';
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
        body: JSON.stringify(body),
      });
      if (response.status === 429) {
        result = 'rate_limit_exceeded';
        setError(result);
        return;
      }
      const data: EmailLinkResponse = await response.json();
      const value = data?.[responseKey];
      if (response.ok && typeof value === 'string' && value) {
        result = 'success';
        return value;
      }
      result = data?.error || 'unexpected_error';
      setError(result);
    } catch {
      setError('unexpected_error');
    } finally {
      pending.current = false;
      setLoading(false);
      analytics.trackingEvent({
        page: pageId,
        action: 'submitted',
        subject: responseKey === 'token' ? 'resend_otp' : 'email_link',
        attributes: { result },
      });
    }
  };

  return { submit, isLoading, error };
};
